import React, { useState, useEffect } from "react";
import Layout from "./Layout/Layout";
import { AiOutlineCloseCircle, AiOutlineDownload } from "react-icons/ai";
import { GrPrevious, GrNext } from "react-icons/gr";
import { FiDownloadCloud } from "react-icons/fi";
import { categories } from "./Data";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { motion, AnimatePresence } from "framer-motion";

const Gallery = () => {
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);
  const [isZipping, setIsZipping] = useState(false);

  useEffect(() => {
    if (!categories.some(cat => cat.year === "2026")) {
      setSelectedYear("2025");
    }
  }, []);

  const filteredImages = categories
    .filter(
      (cat) =>
        cat.year === selectedYear &&
        (selectedCategory === "All" || cat.name === selectedCategory)
    )
    .flatMap((cat) => cat.images);

  const openImage = (image) => setSelectedImage(image);
  const closeImage = () => setSelectedImage(null);

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.indexOf(selectedImage);
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = filteredImages.length - 1;
    if (newIndex >= filteredImages.length) newIndex = 0;
    setSelectedImage(filteredImages[newIndex]);
  };

  const handleDownload = (imageUrl) => {
    const filename = imageUrl.split('/').pop() || 'image.jpg';
    saveAs(imageUrl, filename);
  };

  const handleDownloadAll = async () => {
    setIsZipping(true);
    const zip = new JSZip();
    
    const promises = filteredImages.map(async (image, index) => {
      const imgUrl = image.full || image;
      try {
        const response = await fetch(imgUrl);
        const blob = await response.blob();
        const filename = imgUrl.split('/').pop() || `image_${index + 1}.jpg`;
        zip.file(filename, blob);
      } catch (err) {
        console.error("Error downloading image", err);
      }
    });

    await Promise.all(promises);
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${selectedCategory}_${selectedYear}_images.zip`);
    setIsZipping(false);
  };

  const categoryNames = Array.from(
    new Set(
      categories
        .filter((cat) => cat.year === selectedYear)
        .map((cat) => cat.name)
    )
  );

  return (
    <Layout>
      <div className="min-h-screen bg-[#faf7f2] relative overflow-hidden px-4 sm:px-6 py-12 md:py-24">
        {/* Authentic Background Pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%238b4513\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16 relative z-10"
        >
          <span className="text-orange-700 font-medium text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4 block">
            Visual Memories
          </span>
          <h2 className="text-3xl md:text-6xl font-serif font-bold text-[#4a3320] tracking-wide mb-4 md:mb-6">
            Darshan & Gallery
          </h2>
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="h-[1px] w-8 md:w-12 bg-orange-300"></div>
            <div className="w-2 h-2 md:w-3 md:h-3 rotate-45 bg-orange-500"></div>
            <div className="h-[1px] w-8 md:w-12 bg-orange-300"></div>
          </div>
          <p className="text-stone-600 max-w-2xl mx-auto font-light text-sm md:text-lg leading-relaxed px-2">
            Capturing the divine kirtans, joyful celebrations, and community initiatives of Radha Krishna Gram Vikas Kendra.
          </p>
        </motion.div>

        {/* Filter Controls Board */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-md border border-orange-100/50 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] max-w-6xl mx-auto p-4 md:p-8 mb-10 md:mb-16 flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8 relative z-10"
        >
          {/* Year Segmented Switcher */}
          <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4 w-full lg:w-auto">
            <span className="text-stone-500 text-[10px] md:text-xs font-bold uppercase tracking-widest">Year</span>
            <div className="bg-stone-50 p-1.5 rounded-full flex gap-1 border border-stone-200 w-full sm:w-auto justify-center">
              {["2024", "2025","2026"].map((yr) => (
                <button
                  key={yr}
                  onClick={() => {
                    setSelectedYear(yr);
                    const categoryExists = categories.some(
                      (cat) => cat.year === yr && cat.name === selectedCategory
                    );
                    setSelectedCategory(categoryExists ? selectedCategory : "All");
                    setVisibleCount(12);
                    closeImage();
                  }}
                  className={`flex-1 sm:flex-none px-4 md:px-6 py-2 md:py-2.5 rounded-full font-semibold text-xs md:text-sm transition-all duration-300 relative ${
                    selectedYear === yr
                      ? "text-orange-900"
                      : "text-stone-500 hover:text-stone-800"
                  }`}
                >
                  {selectedYear === yr && (
                    <motion.div
                      layoutId="yearBackground"
                      className="absolute inset-0 bg-orange-100 rounded-full"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{yr}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4 w-full lg:w-auto justify-center flex-1 overflow-hidden">
            <span className="text-stone-500 text-[10px] md:text-xs font-bold uppercase tracking-widest hidden sm:block">Event</span>
            <div 
              className="flex overflow-x-auto sm:flex-wrap justify-start sm:justify-center gap-2 w-full pb-2 md:pb-0 snap-x scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <style dangerouslySetInnerHTML={{__html: `::-webkit-scrollbar { display: none; }`}} />
              <button
                onClick={() => {
                  setSelectedCategory("All");
                  setVisibleCount(12);
                }}
                className={`snap-center shrink-0 px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 ${
                  selectedCategory === "All"
                    ? "bg-orange-500 text-white shadow-md shadow-orange-500/20"
                    : "bg-white border border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-600"
                }`}
              >
                All Events
              </button>
              {categoryNames.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setVisibleCount(12);
                  }}
                  className={`snap-center shrink-0 px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-orange-500 text-white shadow-md shadow-orange-500/20"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Download All Button */}
          <div className="flex items-center w-full lg:w-auto justify-center">
            <button
              onClick={handleDownloadAll}
              disabled={isZipping || filteredImages.length === 0}
              className="group flex items-center gap-2 px-6 py-3 bg-[#4a3320] text-white rounded-full font-medium text-xs md:text-sm hover:bg-[#382618] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl w-full sm:w-auto justify-center"
            >
              {isZipping ? (
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                  <FiDownloadCloud className="w-4 h-4 md:w-5 md:h-5" />
                </motion.div>
              ) : (
                <FiDownloadCloud className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-y-0.5 transition-transform" />
              )}
              {isZipping ? "Preparing Zip..." : "Download Gallery"}
            </button>
          </div>
        </motion.div>

        {/* Image Grid */}
        <AnimatePresence mode="wait">
          {filteredImages.length === 0 ? (
            <motion.div 
              key="empty-state"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-center py-16 md:py-24 text-stone-400 font-light text-sm md:text-lg w-full flex flex-col items-center justify-center"
            >
              <div className="mb-4 text-stone-300 text-5xl md:text-6xl">🪔</div>
              No memories found for this selection.
            </motion.div>
          ) : (
            <motion.div 
              layout
              key="grid-state"
              className="flex flex-col items-center gap-8 md:gap-12 relative z-10 w-full"
            >
              <motion.div 
                layout
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 max-w-7xl mx-auto w-full"
              >
                <AnimatePresence>
                  {filteredImages.slice(0, visibleCount).map((image, index) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, delay: index % 12 * 0.05 }}
                      key={`${image.thumb || image}-${index}`}
                      onClick={() => openImage(image)}
                      className="group cursor-pointer rounded-xl md:rounded-2xl overflow-hidden bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-stone-100 transition-all duration-500"
                    >
                      {/* Image Frame */}
                      <div className="relative h-40 sm:h-48 md:h-64 overflow-hidden bg-stone-100">
                        <img
                          src={image.thumb || image}
                          alt={`${selectedCategory} event`}
                          loading="lazy"
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:translate-y-0 translate-y-4">
                          <span className="bg-white/90 text-[#4a3320] font-medium px-4 md:px-6 py-1.5 md:py-2 rounded-full text-[10px] md:text-sm shadow-lg backdrop-blur-sm">
                            View Memory
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
              
              {visibleCount < filteredImages.length && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setVisibleCount((prev) => prev + 12)}
                  className="px-8 md:px-10 py-3 md:py-3.5 bg-transparent border-2 border-orange-300 text-orange-800 rounded-full font-medium text-xs md:text-sm hover:bg-orange-50 shadow-sm transition-all duration-300"
                >
                  Load More Memories
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Fullscreen Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/95 flex flex-col items-center justify-center z-50 backdrop-blur-xl"
              onClick={closeImage}
            >
              <div className="relative w-full h-full flex items-center justify-center p-2 md:p-12">
                <motion.img
                  key={selectedImage.full || selectedImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  src={selectedImage.full || selectedImage}
                  alt="Enlarged gallery view"
                  className="max-h-[85vh] max-w-[100vw] md:max-w-[85vw] object-contain rounded-lg md:shadow-2xl cursor-grab active:cursor-grabbing"
                  onClick={(e) => e.stopPropagation()}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.7}
                  onDragEnd={(e, { offset }) => {
                    if (offset.x < -60) navigateImage(1);
                    if (offset.x > 60) navigateImage(-1);
                  }}
                />
                
                {/* Top Bar */}
                <div 
                  className="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent pointer-events-auto z-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-white/80 font-medium text-[10px] md:text-sm tracking-widest uppercase truncate pr-4 max-w-[65%]">
                    {selectedCategory === "All" ? "RKGVK Event" : selectedCategory} • {selectedYear}
                  </div>
                  <div className="flex gap-2 md:gap-4">
                    <button
                      className="text-white/70 hover:text-white transition-colors p-2 bg-white/10 rounded-full backdrop-blur-md"
                      onClick={() => handleDownload(selectedImage.full || selectedImage)}
                      title="Download Image"
                    >
                      <AiOutlineDownload className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                    <button
                      className="text-white/70 hover:text-white transition-colors p-2 bg-white/10 rounded-full backdrop-blur-md"
                      onClick={closeImage}
                      title="Close"
                    >
                      <AiOutlineCloseCircle className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                  </div>
                </div>

                {/* Counter */}
                <div 
                  className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs md:text-sm font-medium tracking-widest bg-black/40 px-4 py-1.5 md:py-2 rounded-full backdrop-blur-md pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {filteredImages.indexOf(selectedImage) + 1} / {filteredImages.length}
                </div>

                {/* Navigation Buttons (Hidden on mobile, rely on swipe) */}
                <button
                  className="hidden md:block absolute left-4 md:left-12 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white bg-black/20 hover:bg-black/50 p-4 rounded-full backdrop-blur-md transition-all duration-300"
                  onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
                >
                  <GrPrevious className="w-8 h-8 filter invert" />
                </button>

                <button
                  className="hidden md:block absolute right-4 md:right-12 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white bg-black/20 hover:bg-black/50 p-4 rounded-full backdrop-blur-md transition-all duration-300"
                  onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
                >
                  <GrNext className="w-8 h-8 filter invert" />
                </button>
                
                {/* Swipe Indicator (Mobile only, shown briefly) */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0] }}
                  transition={{ delay: 1, duration: 2, times: [0, 0.2, 0.8, 1] }}
                  className="md:hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/60 text-white/90 px-4 py-2 rounded-full text-xs font-medium backdrop-blur-sm pointer-events-none"
                >
                  Swipe to navigate
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default Gallery;
